/**
 * Design Crop Upload Utilities (Stufe 4.3)
 *
 * Handles the lifecycle of design crop images:
 * 1. computeShortHash() — Content-based dirty detection
 * 2. dataURLtoBlob() — DataURL → Blob conversion for S3 upload
 * 3. uploadDesignCrops() — Batch upload of dirty crops, returns S3 URLs
 *
 * The upload flow integrates with saveDesign():
 * - Before buildEnrichedProductConfig(), we upload any dirty crops
 * - Only S3 URLs (not DataURLs) are sent to the backend
 * - Hash tracking prevents redundant re-uploads
 */

import { uploadAsset } from '@/api/assets';
import { SYSTEM_LAYER_NAME_SET } from '@designdeck/shared';
import { logger } from '@/utils/logger';

// =============================================================================
// HASH COMPUTATION
// =============================================================================

/**
 * Compute a short content hash from canvas objects JSON.
 * Used for dirty-detection: if the hash hasn't changed since last upload,
 * we skip re-uploading the crop image.
 *
 * Filters system layers before hashing (they don't affect the crop).
 * Uses FNV-1a for speed (no crypto needed — this isn't security-critical).
 *
 * @param objects - Canvas objects array (from canvasJSON.objects)
 * @returns 8-character hex hash string, or null if no user objects
 */
export function computeShortHash(
  objects: Array<Record<string, unknown>> | undefined
): string | null {
  if (!objects || objects.length === 0) return null;

  // Filter system layers (same logic as computeRelativeDesignStateFromJSON)
  const userObjects = objects.filter(
    (obj) => !SYSTEM_LAYER_NAME_SET.has((obj.name as string) ?? '')
  );

  if (userObjects.length === 0) return null;

  // Deterministic stringify: collect ALL unique keys across ALL objects
  const allKeys = new Set<string>();
  for (const obj of userObjects) {
    for (const key of Object.keys(obj)) {
      allKeys.add(key);
    }
  }
  const sortedKeys = [...allKeys].sort();
  const str = JSON.stringify(userObjects, sortedKeys);

  // FNV-1a 32-bit hash
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }

  // Convert to 8-char hex (unsigned)
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// =============================================================================
// DATA URL → BLOB CONVERSION
// =============================================================================

/**
 * Convert a data URL (base64-encoded) to a Blob.
 *
 * @param dataUrl - data:image/png;base64,... string
 * @returns Blob ready for upload, or null if conversion fails
 */
export function dataURLtoBlob(dataUrl: string): Blob | null {
  try {
    const [header, base64Data] = dataUrl.split(',');
    if (!header || !base64Data) return null;

    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch?.[1] ?? 'image/png';

    const byteString = atob(base64Data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([byteArray], { type: mime });
  } catch (error) {
    logger.warn(
      'CropUpload',
      'Failed to convert DataURL to Blob',
      error instanceof Error ? error.message : String(error)
    );
    return null;
  }
}

// =============================================================================
// CROP UPLOAD
// =============================================================================

/** Input for a single position's crop upload */
export interface CropUploadInput {
  positionSlug: string;
  /** Fresh DataURL from canvas (data:image/png;base64,...) */
  designCropDataUrl: string;
  /** Canvas objects for hash computation */
  canvasObjects: Array<Record<string, unknown>> | undefined;
  /** Hash from last successful S3 upload (skip if unchanged) */
  lastUploadedCropHash: string | null;
}

/** Result of a single position's crop upload */
export interface CropUploadResult {
  positionSlug: string;
  /** S3 URL for the uploaded crop */
  s3Url: string;
  /** Content hash of the uploaded crop */
  contentHash: string;
}

/**
 * Upload dirty design crops to S3 in parallel.
 *
 * For each position:
 * 1. Compute content hash from canvas objects
 * 2. Skip if hash matches lastUploadedCropHash (not dirty)
 * 3. Convert DataURL to Blob → File
 * 4. Upload to S3 via uploadAsset()
 * 5. Return S3 URL + hash for state update
 *
 * Non-blocking error handling: if a single crop fails, it's logged and skipped.
 * The save continues with positions that succeeded.
 *
 * @param inputs - Array of positions with crop data to potentially upload
 * @param designId - Design ID for organizing assets
 * @returns Array of successful upload results
 */
export async function uploadDesignCrops(
  inputs: CropUploadInput[],
  designId: string
): Promise<CropUploadResult[]> {
  const uploads: Array<Promise<CropUploadResult | null>> = [];

  for (const input of inputs) {
    const contentHash = computeShortHash(input.canvasObjects);

    // Skip if no user objects or hash unchanged (not dirty)
    if (!contentHash || contentHash === input.lastUploadedCropHash) {
      continue;
    }

    uploads.push(
      (async (): Promise<CropUploadResult | null> => {
        try {
          const blob = dataURLtoBlob(input.designCropDataUrl);
          if (!blob) {
            logger.warn(
              'CropUpload',
              `Failed to convert crop DataURL for ${input.positionSlug}`
            );
            return null;
          }

          // Create File from Blob (uploadAsset expects File)
          const file = new File(
            [blob],
            `crop-${input.positionSlug}-${contentHash}.png`,
            { type: 'image/png' }
          );

          const asset = await uploadAsset(file, { designId });

          logger.debug(
            'CropUpload',
            `Uploaded crop for ${input.positionSlug}: ${asset.url} (hash: ${contentHash})`
          );

          return {
            positionSlug: input.positionSlug,
            s3Url: asset.url,
            contentHash,
          };
        } catch (error) {
          // Non-blocking: log and skip failed uploads
          logger.warn(
            'CropUpload',
            `Failed to upload crop for ${input.positionSlug}`,
            error instanceof Error ? error.message : String(error)
          );
          return null;
        }
      })()
    );
  }

  if (uploads.length === 0) return [];

  const results = await Promise.all(uploads);
  return results.filter((r): r is CropUploadResult => r !== null);
}
